// ============================================
// FILE: src/components/fashion/MediaLightbox.jsx
// ============================================
//
// Full-screen viewer for the product gallery.
//
// z-[340] puts it above the product screen (300) and the size guide (320).
// Videos are carried through rather than skipped, so the index the shopper
// clicked always lines up with what they were looking at.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Play } from 'lucide-react';
import {
  isVideo,
  getMediaFull,
  getMediaZoom,
  getMediaThumb,
  getMediaPoster,
  getEmbedUrl,
} from '../../utils/mediaHelpers';

const SWIPE_THRESHOLD = 50;

const MediaLightbox = ({
  media = [],
  startIndex = 0,
  isOpen = false,
  onClose = () => {},
  productName = '',
  isDesktop = false,
}) => {
  const [index, setIndex] = useState(startIndex);
  const [zoomed, setZoomed] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const touchStartX = useRef(null);
  const videoRefs = useRef({});

  const count = media.length;

  const go = useCallback((next) => {
    if (next < 0 || next > count - 1) return;
    setIndex(next);
    setZoomed(false);
    setPlayingId(null);
  }, [count]);

  // Re-enter at whichever image was clicked.
  useEffect(() => {
    if (isOpen) {
      setIndex(startIndex);
      setZoomed(false);
      setPlayingId(null);
    }
  }, [isOpen, startIndex]);

  // Arrow keys and Escape — expected of anything full-screen on desktop.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(index + 1);
      else if (e.key === 'ArrowLeft') go(index - 1);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, index, go, onClose]);

  // Stop a video that scrolls out of view, or its audio keeps playing.
  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([id, el]) => {
      if (el && String(media[index]?.id) !== String(id)) el.pause();
    });
  }, [index, media]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isOpen]);

  if (!isOpen || count === 0) return null;

  const current = media[index];
  const currentIsVideo = isVideo(current);

  const renderStage = () => {
    if (!currentIsVideo) {
      return (
        <img
          // The zoom source, not the stage source — this view scales past
          // 100%, so it needs the largest derivative rather than the one
          // sized for a 300px grid cell.
          src={getMediaZoom(current)}
          alt={current.alt_text || `${productName} image ${index + 1}`}
          onClick={() => setZoomed((z) => !z)}
          className="select-none"
          style={{
            maxWidth: zoomed ? 'none' : '100%',
            maxHeight: zoomed ? 'none' : '100%',
            width: zoomed ? '160%' : 'auto',
            objectFit: 'contain',
            cursor: zoomed ? 'zoom-out' : 'zoom-in',
            transition: 'width 200ms ease',
          }}
          draggable={false}
        />
      );
    }

    const playing = String(playingId) === String(current.id);
    const poster = getMediaPoster(current);

    if (!playing) {
      return (
        <button
          type="button"
          onClick={() => setPlayingId(current.id)}
          className="relative max-w-full max-h-full"
          aria-label="Play video"
        >
          {poster && (
            <img
              src={poster}
              alt={current.alt_text || `${productName} video`}
              className="max-w-full max-h-[80vh] object-contain opacity-90"
            />
          )}
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center">
              <Play size={26} className="text-[#282c3f] ml-1" fill="currentColor" />
            </span>
          </span>
        </button>
      );
    }

    if (current.provider === 'upload') {
      return (
        <video
          ref={(el) => { videoRefs.current[current.id] = el; }}
          src={getMediaFull(current)}
          poster={poster || undefined}
          controls
          playsInline
          autoPlay
          preload="metadata"
          className="max-w-full max-h-[85vh]"
        />
      );
    }

    return (
      <iframe
        src={getEmbedUrl(current, { autoplay: true })}
        title={current.alt_text || `${productName} video`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="border-0"
        style={{ width: isDesktop ? '80vw' : '100vw', height: isDesktop ? '80vh' : '56vw' }}
      />
    );
  };

  return (
    <div className="fixed inset-0 z-[340] bg-black flex flex-col" role="dialog" aria-modal="true">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>
          {index + 1} / {count}
        </span>

        <div className="flex items-center gap-1">
          {!currentIsVideo && (
            <button
              type="button"
              onClick={() => setZoomed((z) => !z)}
              className="p-2 rounded-full hover:bg-white/10"
              aria-label={zoomed ? 'Zoom out' : 'Zoom in'}
            >
              {zoomed
                ? <ZoomOut size={20} className="text-white" />
                : <ZoomIn size={20} className="text-white" />}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10"
            aria-label="Close"
          >
            <X size={22} className="text-white" />
          </button>
        </div>
      </div>

      {/* Stage */}
      <div
        className={`flex-1 min-h-0 flex items-center justify-center relative ${zoomed ? 'overflow-auto' : 'overflow-hidden'}`}
        onTouchStart={(e) => { touchStartX.current = e.touches?.[0]?.clientX ?? null; }}
        onTouchEnd={(e) => {
          if (touchStartX.current == null || zoomed) return;
          const dx = (e.changedTouches?.[0]?.clientX ?? touchStartX.current) - touchStartX.current;
          touchStartX.current = null;
          if (Math.abs(dx) < SWIPE_THRESHOLD) return;
          go(dx < 0 ? index + 1 : index - 1);
        }}
      >
        {renderStage()}

        {isDesktop && count > 1 && !zoomed && (
          <>
            {index > 0 && (
              <button
                type="button"
                onClick={() => go(index - 1)}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                aria-label="Previous image"
              >
                <ChevronLeft size={24} className="text-white" />
              </button>
            )}
            {index < count - 1 && (
              <button
                type="button"
                onClick={() => go(index + 1)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                aria-label="Next image"
              >
                <ChevronRight size={24} className="text-white" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Thumbnail rail */}
      {count > 1 && (
        <div className="flex-shrink-0 flex gap-2 overflow-x-auto hide-scrollbar px-4 py-3 justify-start sm:justify-center">
          {media.map((item, i) => (
            <button
              key={item.id ?? i}
              type="button"
              onClick={() => go(i)}
              className="relative flex-shrink-0 overflow-hidden"
              style={{
                width: 48,
                height: 62,
                borderRadius: 2,
                outline: i === index ? '2px solid #fff' : '1px solid rgba(255,255,255,0.25)',
                outlineOffset: -1,
                opacity: i === index ? 1 : 0.55,
              }}
              aria-label={`Show image ${i + 1}`}
            >
              <img src={getMediaThumb(item)} alt="" className="w-full h-full object-cover" loading="lazy" />
              {isVideo(item) && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <Play size={13} className="text-white" fill="currentColor" />
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaLightbox;
